import { useQuery } from '@tanstack/vue-query';
import { decodeJwt } from 'jose/jwt/decode';
import { computed } from 'vue';

import { ACCESS_TOKEN_STORAGE_KEY } from '~/constants';
import KnownError from '~/errors/KnownError';
import RetryAuthError from '~/errors/RetryAuthError';
import localstorage from '~/foundation/storage/localstorage';
import keycloackApi from '~/services/keycloack-api/keycloack-api';
import { KeycloackApi } from '~/services/keycloack-api/keycloack-api.types';
import personApi from '~/services/person-api/person-api';

import { queryKeys } from '../queryClient';

export default function useAuth() {
  const queryKey = queryKeys.AUTH.STATUS;

  const queryFn = async () => {
    const token = localstorage.get<string>(ACCESS_TOKEN_STORAGE_KEY);
    if (!token) throw new RetryAuthError('NO_TOKEN');

    const decoded = decodeJwt<KeycloackApi.JSONPayload>(token);
    if (!decoded.sub) throw new RetryAuthError('INVALID_TOKEN');

    const realmRoles = decoded.realm_access?.roles ?? [];
    const fintechBackofficeRoles = decoded.resource_access?.['fintech-backoffice']?.roles ?? [];

    const hasFintechBackofficeAccess = computed(() => fintechBackofficeRoles.includes('fintech_backoffice_access'));
    const hasAdminRole = computed(() => realmRoles.includes('admin-access'));

    if (!hasFintechBackofficeAccess.value || !hasAdminRole.value) {
      throw new KnownError('NOT_ADMIN');
    }

    const [keycloack, person] = await Promise.all([
      keycloackApi.getUserInfo(),
      personApi.user.getById(decoded.sub),
    ]);

    const allowedRoles = [...fintechBackofficeRoles];
    const groups = decoded.groups ?? [];

    const normalizeGroup = (group: string) => group.replace(/^\//, '');

    const hasSomeRole = (accepted: string[]) => accepted.some((r) => allowedRoles.includes(r));

    const hasRole = (role: string) => hasSomeRole([role]);

    const hasSomeGroup = (accepted: string[]) => accepted.some((g) =>
      groups.some((userGroup) => normalizeGroup(userGroup) === normalizeGroup(g)),
    );

    const hasGroup = (group: string) => hasSomeGroup([group]);

    return {
      allowedRoles,
      fintechBackofficeRoles,
      groups,
      hasGroup,
      hasRole,
      hasSomeGroup,
      hasSomeRole,
      keycloack,
      person,
    };
  };

  return useQuery({
    queryFn,
    queryKey,
    refetchInterval: 30_000, // 30s
    refetchOnWindowFocus: true,
    retry: 3,
    retryDelay: 500,
    retryOnMount: true,
    staleTime: 30_000, // 30s
  });
}
